<template>
    <div class="premium-page">
        <!-- Hero, from the Figma mock (Landing Page, node 4800:363).
             A gold field, the pitch as one long sentence resolving into the
             product name, and the shop's own artwork tiling down the right. -->
        <header class="premium-hero">
            <div class="premium-hero-inner">
                <div class="premium-hero-copy">
                    <!-- One h1, not a wordmark image and a separate headline.
                         The sentence *is* the heading here, and it ends on the
                         product name — splitting it into two elements would
                         put a heading break in the middle of a sentence. -->
                    <h1 class="premium-headline">
                        Get more rewards every season and unlock new content on
                        the best gaming bot out there with
                        <!-- No whitespace inside this span. It is `nowrap`, so
                             the newline that used to sit between the crown and
                             </span> was preserved as a real space and hung off
                             the end of the line. The word gap before "Gamebot"
                             is the ordinary space above, not a margin. -->
                        <span class="premium-headline-brand">Gamebot Premium<i class="premium-headline-crown" aria-hidden="true"></i></span>
                    </h1>

                    <div class="premium-hero-actions">
                        <!-- Jumps to the plans rather than straight to Stripe.
                             With three tiers the hero can't presume which one
                             someone wants, and sending them to checkout for the
                             base plan would quietly make that choice for them. -->
                        <!-- Nothing for a subscriber. This used to be a
                             "Manage subscription" button, which put the one
                             action an existing member wants on the page whose
                             job is selling to people who aren't one — and made
                             the hero's biggest control mean two different
                             things depending on who was looking.

                             Managing now lives at the bottom of the Customize
                             tab, with the rest of what you own. The status
                             card directly below is the signpost to it, and
                             the note beside this is the hero's whole message
                             to someone already in. -->
                        <b-button
                            v-if="!isPremium"
                            class="premium-cta"
                            @click="scrollTo('plans')">
                            Get started
                        </b-button>

                        <p class="premium-hero-note">
                            <template v-if="isPremium">
                                You're already in. Thanks.
                                <img class="hero-note-crown" src="@/assets/images/premium/crown.svg" alt="" />
                            </template>
                            <template v-else>From {{ premiumPrice }} / mo</template>
                        </p>
                    </div>
                </div>

                <!-- The shop's artwork, tiling. The asset is built to repeat
                     vertically, so it is `repeat-y` rather than a fixed 544px
                     crop as in the mock — at any hero height the seam lands
                     where the artwork intends rather than mid-row.
                     aria-hidden: it is decoration, and naming 40 pack shots
                     to a screen reader is noise. -->
                <div class="premium-hero-pattern" aria-hidden="true"></div>
            </div>
        </header>

        <div class="premium-body">
            <!-- Your subscription. Above the pitch, because someone who
                 already pays came here to manage it, not to be sold to. -->
            <!-- The whole card is the link, not a button inside it.
                 Managing a subscription now lives at the bottom of the
                 Customize tab, next to the other things you own and control,
                 rather than on the page that sells it to you. This card is
                 the signpost: someone who already pays came here to change
                 something, and the entire box is a bigger target than a
                 button in its corner. -->
            <router-link
                v-if="subscription.state !== 'none'"
                class="box subscription-box"
                :class="`is-${subscription.state}`"
                to="/profile/me/customize">
                <div class="level is-mobile subscription-level">
                    <div class="level-left">
                        <div>
                            <p class="subscription-state">
                                <!-- The brand's own crown for "active", not
                                     FontAwesome's — same reasoning as the
                                     profile page and the nav. -->
                                <img
                                    v-if="subscription.icon === 'crown'"
                                    class="subscription-state-crown"
                                    src="@/assets/images/premium/crown.svg"
                                    alt="" />
                                <b-icon v-else :icon="subscription.icon" size="is-small" />
                                {{ subscription.heading }}
                            </p>
                            <p class="subscription-detail">{{ subscription.detail }}</p>
                        </div>
                    </div>
                    <div class="level-right">
                        <span class="subscription-go">
                            Manage
                            <b-icon icon="angle-right" size="is-small" />
                        </span>
                    </div>
                </div>
            </router-link>

            <!-- Show the features working before asking for the card. The
                 plan columns below still list them, for anyone who scrolled
                 past or is here to compare prices. -->
            <PremiumShowcase :features="premium.features" :name="username" />

            <!-- The turn from showing to asking. The demos above have made
                 every argument; this is the handoff to the prices, in the
                 same gradient-panel shape the homepage closes with. -->
            <section class="premium-turn">
                <div class="turn-panel">
                    <h2 class="title is-3 turn-title">Ready when you are.</h2>
                    <p class="turn-sub">Pick the plan that fits your servers.</p>
                    <b-button
                        type="is-light"
                        size="is-medium"
                        class="turn-cta"
                        @click="scrollTo('plans')">
                        See the plans
                    </b-button>
                </div>
            </section>

            <!-- What a server actually gets, once. Sits right after the CTA
                 rather than after the pricing — the pass is part of what
                 "Ready when you are" is selling, not an appendix to it.
                 Repeating it per column would triple the page to say the same
                 thing.

                 Drawn as an actual pass — stub, perforation, notches — because
                 "one per server" is a thing you hold, and a plain box of
                 bullets was indistinguishable from the plan cards below it. -->
            <section id="server-pass" ref="serverPass" class="box pass-section">
                <!-- Half-overlapping bubble, same device as the credits in the
                     shop's purchase modal. The pass is an icon here, not a
                     headline — the section needs a title of its own, and the
                     artwork shouldn't be standing in for one. -->
                <span class="pass-bubble">
                    <img
                        src="@/assets/images/premium/server-pass.svg"
                        alt=""
                        class="pass-icon" />
                </span>

                <h2 class="title is-4 pass-title">
                    Gamebot {{ pass.name }}
                    <b-tag rounded type="is-light" class="pass-coming-soon">Coming soon</b-tag>
                </h2>
                <p class="pass-tagline">{{ pass.tagline }}</p>

                <ul class="pass-features">
                    <li
                        v-for="feature in pass.features"
                        :key="feature.title"
                        class="media pass-feature"
                        :class="{ 'is-highlighted': feature.highlight }">
                        <figure class="media-left">
                            <span class="feature-icon">
                                <b-icon :icon="feature.icon" size="is-small" />
                            </span>
                        </figure>
                        <div class="media-content">
                            <p class="feature-title">{{ feature.title }}</p>
                            <p class="feature-body">{{ feature.body }}</p>
                        </div>
                    </li>
                </ul>
            </section>

            <hr class="premium-rule" />

            <!-- The whole offer: three plans differing only in how many
                 servers get a leaderboard. Passes aren't sold separately, so
                 there's one decision to make rather than a matrix. -->
            <section id="plans" ref="plans" class="plans">
                <h2 class="title is-4">How many servers do you run?</h2>
                <p class="subtitle is-6">
                    Every plan is Gamebot Premium. Servers get cheaper the more you carry.
                    <a class="pass-link" @click="scrollTo('serverPass')">
                        Learn more about Server Pass
                        <b-icon icon="angle-right" size="is-small" />
                    </a>
                </p>

                <!-- Tri-column from desktop only. Three columns inside a
                     tablet leaves each box ~234px wide, where every checklist
                     line except "Double XP" wraps — a ragged mix of one- and
                     two-line rows. Full width until there's room for three. -->
                <div class="columns is-multiline is-desktop plan-columns">
                    <div v-for="plan in plans" :key="plan.id" class="column is-4-desktop">
                        <!-- No badge above the recommended column any more.
                             "Most popular" was a claim about which plan people
                             choose, on a plan they currently can't. The border
                             alone marks the recommendation without asserting
                             anything we'd have to defend. -->
                        <div class="box plan-box" :class="{ 'is-recommended': plan.recommended }">

                            <!-- The plan as an equation: Premium plus N passes.
                                 One pass drawing throughout with a multiplier
                                 after it, rather than a stacked drawing for
                                 some tiers and a number for others — one way
                                 of saying it, so the three cards compare. -->
                            <h3 class="plan-name">
                                <!-- "Premium" rather than the full "Gamebot
                                     Premium": the section heading above has
                                     already said which product this is, and
                                     the shorter mark leaves room to set it
                                     larger instead of squeezing it to fit
                                     beside the passes. -->
                                <img
                                    class="plan-wordmark"
                                    src="@/assets/images/premium/premium-crown.svg"
                                    :alt="plan.name" />
                                <!-- Only the tiers that actually cover a
                                     server get the pass icon — the base plan
                                     doesn't bundle one (see PLANS), and
                                     showing the icon with no count beside it
                                     read as included when it isn't. -->
                                <template v-if="plan.servers > 0">
                                    <span class="plan-plus" aria-hidden="true">+</span>
                                    <span class="plan-passes">
                                        <img class="plan-pass" :src="passArt" alt="" />
                                        <span v-if="plan.servers > 1" class="plan-multiplier">
                                            &times;&thinsp;{{ plan.servers }}
                                        </span>
                                    </span>
                                </template>
                            </h3>

                            <p class="plan-servers">{{ plan.serversLabel }}</p>

                            <p class="plan-price">
                                <span class="plan-amount">{{ plan.price }}</span>
                                <span class="plan-interval">/mo</span>
                            </p>
                            <!-- The struck-through list price and the badge are
                                 the same arithmetic, so they can't disagree. -->
                            <p class="plan-compare">
                                <template v-if="plan.saving">
                                    <s class="plan-list">{{ plan.listPrice }}</s>
                                    <b-tag type="is-light">Save {{ plan.saving }}%</b-tag>
                                </template>
                                <span v-else class="plan-list-placeholder">{{ plan.tagline }}</span>
                            </p>

                            <ul class="plan-checklist">
                                <li v-for="line in plan.lines" :key="line">
                                    <b-icon icon="check" size="is-small" type="is-gold" />
                                    {{ line }}
                                </li>
                            </ul>

                            <!-- A tier Stripe has no Price for is shown but
                                 can't be bought; a button that 503s at the
                                 last step is worse than an honest label. -->
                            <!-- A subscriber gets a link, not a purchase.
                                 This button used to call subscribe(), which
                                 quietly branched to Stripe's billing portal
                                 when you already paid — so picking a *different*
                                 tier and managing your current one were the
                                 same click, and which one you got depended on
                                 state the button didn't show. Every route to
                                 the portal now goes through the Customize tab,
                                 so there is one door to Stripe and it is
                                 somewhere you can find again. -->
                            <!-- Availability is decided before subscription
                                 state, and the order is the whole point. A
                                 tier Stripe has no Price for cannot be bought
                                 by anyone, subscriber or not — putting
                                 `isPremium` first meant a subscriber saw
                                 "Manage subscription" on the Server Pass
                                 column, which is descoped and says "Coming
                                 soon" to everyone else. That is the same
                                 wrong-column confusion this change set out to
                                 remove, one branch further along. -->
                            <b-button v-if="plan.action === 'unavailable'" expanded disabled>Coming soon</b-button>
                            <b-button
                                v-else-if="plan.action === 'manage'"
                                tag="router-link"
                                to="/profile/me/customize"
                                type="is-light"
                                expanded>
                                Manage subscription
                            </b-button>
                            <b-button
                                v-else
                                :type="plan.recommended ? 'is-gold' : 'is-light'"
                                expanded
                                :loading="working"
                                @click="subscribe(plan.id)">
                                Choose
                            </b-button>

                            <!-- Always rendered, blank on the base plan. The
                                 base plan has no extra servers to price, and
                                 omitting the line let the other two columns'
                                 buttons ride 28px higher than its own. -->
                            <p class="help plan-unit">{{ plan.unitLabel }}</p>
                        </div>
                    </div>
                </div>

                <p class="help has-text-centered plan-fineprint">
                    Cancel any time. What you've claimed stays yours.
                </p>
            </section>

            <!-- Fine print, in the brand's voice rather than a legal block. -->
            <div class="columns is-multiline premium-faq">
                <div v-for="item in faq" :key="item.q" class="column is-4">
                    <div class="box faq-box">
                        <p class="faq-q">{{ item.q }}</p>
                        <p class="faq-a">{{ item.a }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    BASE_PLAN,
    PLANS,
    PREMIUM,
    SERVER_PASS,
    formatPrice,
    formatRenewalDate,
    planExtraServerPrice,
    planAction,
    planListPrice,
    planSaving,
    subscriptionStatus,
} from '../globals/premium.js'
import PremiumShowcase from '../components/Premium/PremiumShowcase.vue'

// Required rather than referenced by path: the build fingerprints asset URLs,
// so a string built at runtime would point at a file that doesn't exist.
//
// One pass drawing for every tier, with a multiplier after it, so the three
// cards are counted the same way. The stacked `3x Server Pass` export isn't
// used any more and isn't kept here; it's still in the brand folder.
const PASS_ART = require('../assets/images/premium/server-pass.svg')

/**
 * The sales page for Gamebot Premium and Server Passes.
 *
 * Copy and pricing live in `globals/premium.js`; this file is layout. The one
 * number that isn't from there is Premium's price, which comes from Stripe so
 * the page can't quote something different from what the card is charged.
 *
 * Styling follows the brand guide: Gamebot black behind the wordmark, Inter
 * throughout, gold reserved for Premium the way it already is for Gamebot
 * Gold.
 */
export default {
    name: 'Premium',
    components: { PremiumShowcase },
    metaInfo: {
        title: 'Gamebot Premium',
    },
    data() {
        return {
            premium: PREMIUM,
            pass: SERVER_PASS,
            faq: [
                {
                    q: 'What happens to my rewards if I cancel?',
                    a: 'Anything you claimed is yours for good. The cosmetics switch off, and premium rewards you never claimed go back to waiting.',
                },
                {
                    q: 'Can I buy Server Passes on their own?',
                    a: "No, all the subscriptions are Premium and the bigger ones just cover more servers. That's one, unified subscription to keep track of all your premium features.",
                },
                {
                    q: 'Do my stats reset?',
                    // Matches behaviour, as of the rollover landing: a season
                    // is a meteorological quarter (config/seasons.js), and
                    // archivePipeline() in server/services/seasons.js clears
                    // stats, xp and level into `history` while deliberately
                    // leaving achievements alone. This answer used to describe
                    // intent rather than fact — nothing called the rollover at
                    // all — which is worth remembering if the copy drifts again.
                    a: "Stats, XP and level all reset every three months, but every season you've played stays in your profile to look back on. Achievements carry over across seasons.",
                },
            ],
        }
    },
    computed: {
        /** The pass drawing, the same one on every tier. */
        passArt() { return PASS_ART },

        working() { return this.$store.state.profile.loading },
        isPremium() { return this.$store.state.profile.premium === true },

        /** Puts the visitor's own name on the crown demo when they're signed in. */
        username() {
            const user = this.$store.getters.getUser
            return (user && user.username) || ''
        },

        /**
         * Stripe's price when we have it, the declared one until then, so the
         * page renders a number rather than a gap on first paint.
         */
        premiumPrice() {
            const price = this.$store.state.profile.price
            return price
                ? formatPrice(price.unitAmount, price.currency)
                : formatPrice(this.premium.priceCents)
        },
        /**
         * The three plans, priced. Stripe's amount wins wherever we have one,
         * so the column can't quote a number the card won't be charged; the
         * declared price is the fallback for first paint and for logged-out
         * visitors.
         *
         * `buyable` is whether Stripe actually has a Price for the tier *and*
         * the tier isn't flagged `passComingSoon` — a tier whose entire
         * differentiator is a feature that doesn't exist yet stays a "Coming
         * soon" button even if someone finishes wiring its Stripe Price
         * early, so turning that on can't accidentally start selling it.
         * `planPrices` only contains plans the server found a Price for.
         */
        plans() {
            const quoted = this.$store.state.profile.planPrices || {}
            const currency = (quoted[BASE_PLAN.id] && quoted[BASE_PLAN.id].currency) || 'usd'
            const priceOf = plan => {
                const stripePrice = quoted[plan.id]
                return stripePrice ? stripePrice.unitAmount : plan.priceCents
            }
            // Savings and list prices are measured against the live base
            // price, so a change in Stripe moves the badge with it.
            const baseCents = priceOf(BASE_PLAN)

            return PLANS.map(plan => {
                const cents = priceOf(plan)
                const priced = { ...plan, priceCents: cents }
                const extra = planExtraServerPrice(priced, baseCents)
                // Once, so `buyable` and the action it feeds cannot drift.
                const buyable = !plan.passComingSoon && Boolean(quoted[plan.id])

                return {
                    ...plan,
                    price: formatPrice(cents, currency),
                    listPrice: formatPrice(planListPrice(plan, baseCents), currency),
                    saving: planSaving(priced, baseCents),
                    // Written as an escape, not a literal non-breaking space.
                    // A literal one is invisible in the source and survives
                    // exactly until someone reflows the line — which is how
                    // serversLabel below ended up with a plain space that HTML
                    // collapsed to nothing.
                    unitLabel: extra === null
                        ? '\u00A0'
                        : `${formatPrice(extra, currency)} per extra server`,
                    buyable,
                    // What its button does — see planAction() for why
                    // availability is decided before who is asking.
                    action: planAction({ buyable }, this.isPremium),
                    // Same escape as unitLabel, and this is the one that
                    // showed: the base plan has no servers to name, so with a
                    // plain space its line collapsed and everything under it —
                    // price, saving, checklist, button — sat 18px higher than
                    // the other two columns.
                    serversLabel: plan.servers > 0
                        ? `${plan.servers} ${plan.servers === 1 ? 'server' : 'servers'}`
                        : '\u00A0',
                    lines: this.planLines(plan),
                }
            })
        },

        /**
         * The subscription card's words. Each state gets its own, because
         * "renews on" and "ends on" are opposite messages and sharing a
         * sentence between them is how you tell someone their cancelled
         * subscription is renewing.
         */
        subscription() {
            const status = subscriptionStatus({
                premium: this.$store.state.profile.premium,
                premiumUntil: this.$store.state.profile.premiumUntil,
                cancelAtPeriodEnd: this.$store.state.profile.cancelAtPeriodEnd,
            })
            const date = formatRenewalDate(status.until)

            const copy = {
                active: {
                    icon: 'crown',
                    heading: 'Gamebot Premium is active',
                    detail: date ? `Renews on ${date}.` : 'Renews automatically.',
                },
                cancelling: {
                    icon: 'clock',
                    heading: 'Ending soon',
                    detail: date
                        ? `You keep everything until ${date}. Restart any time before then.`
                        : 'You keep everything until the end of the period.',
                },
                lapsed: {
                    icon: 'clock',
                    heading: 'Your subscription has ended',
                    detail: 'Anything you claimed is still yours. Resubscribe to pick the track back up.',
                },
            }

            return { state: status.state, ...(copy[status.state] || {}) }
        },

    },
    methods: {
        /**
         * What a column lists. Every plan is Premium, so the Premium features
         * are the same three lines each time. The base plan stops there — it
         * has no Server Pass — and the two bulk tiers add a line for it,
         * flagged as not shipped yet rather than promising something that
         * doesn't work today.
         */
        planLines(plan) {
            const lines = PREMIUM.features.map(feature => feature.title)
            if (plan.servers > 0) {
                const servers = plan.servers === 1
                    ? 'Leaderboards for 1 server'
                    : `Leaderboards for ${plan.servers} servers`
                lines.push(plan.passComingSoon ? `${servers} — coming soon` : servers)
            }
            return lines
        },

        /**
         * Scrolls to a section by ref.
         *
         * A ref rather than an `href="#id"`: this is a vue-router SPA, so a
         * hash link pushes a route and the router's own scroll behaviour takes
         * over. Smooth unless the visitor asked for less motion, in which case
         * jumping is the correct answer rather than a slower animation.
         */
        scrollTo(ref) {
            const el = this.$refs[ref]
            if (!el) return

            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
        },

        /**
         * Straight to Stripe's hosted checkout. Buying only — this used to
         * branch to the billing portal for an existing subscriber, which made
         * one control mean two different things. Managing lives on the
         * Customize tab, and nothing on this page reaches the portal.
         */
        subscribe(plan) {
            return this.$store.dispatch('profile/subscribe', plan)
        },
    },
    mounted() {
        // Public, so the price renders for logged-out visitors too.
        this.$store.dispatch('profile/fetchPremiumPrice')
        if (this.$store.getters.getToken) {
            this.$store.dispatch('profile/fetchPremiumStatus')
        }
    },
}
</script>

<style lang="scss" scoped>
.premium-page {
    text-align: left;
    background-color: $background;
    min-height: 100vh;
}

// Hero
//
// Flat Gamebot black. This was a bespoke conic gradient with a scrim over it —
// the only hero on the site drawn that way, and it cost a scrim, a text-shadow
// and a drop-shadow just to keep white type legible on top of it. Black gives
// the wordmark 19:1 without any of that, and the gold button has something to
// be gold against.
//
// The route opts out of PageGradient rather than layering over it: the canvas
// only covers the top 700px, so it would have been 700px of animated WebGL
// running behind an opaque hero and never seen. That also retires the
// `z-index: 2` this page used to need to sit above it.

// ── Hero ────────────────────────────────────────────────────────────────
//
// Values taken from the Figma mock (Landing Page, node 4800:363) rather than
// eyeballed, so a revision there is a diff here.
//
// ⚠️ $hero-copy is the mock's own #b39d59, which is 1.67:1 against the gold.
// Large text needs 3.0:1, so the sentence that carries the pitch is currently
// the least readable thing in the hero. $hero-brand (#55420a, 7.8:1) is the
// same family and passes — swapping one for the other is the whole fix if
// this gets revised.
// The band's padding, named because the pattern cancels it to reach the
// hero's real top and bottom edges — two places that must agree.
$hero-pad-top: 200px;
$hero-pad-bottom: 90px;

// How far the artwork sits in from the container's right edge. The mock has
// it flush; this is the one number to change if it wants to move again.
$hero-pattern-inset: 3rem;

$hero-field: #e2cc84;
$hero-copy:  #b39d59;
$hero-brand: #55420a;
$hero-pill:  rgba(62, 52, 21, 0.98);
$hero-pill-label: #f6edc8;
$hero-note:  rgba(78, 67, 32, 0.98);

.premium-hero {
    background-color: $hero-field;
    // The mock's 575px band, as padding rather than a fixed height: the
    // sentence wraps to more lines as the viewport narrows, and a fixed
    // height would clip it.
    padding: $hero-pad-top 20px $hero-pad-bottom;
    overflow: hidden;

    @include mobile { padding: 160px 20px 48px; }
}

// `relative` is what the pattern positions against — it anchors to the
// container's right edge, not the viewport's.
.premium-hero-inner {
    position: relative;
    max-width: $widescreen;
    margin: 0 auto;
}

.premium-hero-copy {
    // 38rem is the mock's own 603px of 1440, near enough — the previous
    // 34rem was narrower than the design, which is what pushed the sentence
    // onto an extra line. Capped rather than fluid so the breaks land the
    // same way at any viewport, and so the copy never runs under the
    // artwork, which is out of flow.
    position: relative;
    max-width: 38rem;
}

.premium-headline {
    color: $hero-copy;
    font-size: 3rem;
    font-weight: 700;
    line-height: 1.13;
    letter-spacing: -0.05em;
    margin: 0;

    @include touch  { font-size: 2.25rem; }
    @include mobile { font-size: 1.75rem; letter-spacing: -0.03em; }
}

// The product name, and the only part of the sentence at full contrast.
// Kept on one line so the name never breaks across two, and so the crown
// stays with the word it belongs to.
.premium-headline-brand {
    color: $hero-brand;
    white-space: nowrap;
}

// The crown, as a mask rather than an <img>.
//
// crown.svg has `fill="#C3AB4C"` baked into all six paths, so as an image it
// is stuck at that gold whatever the surrounding text does. Masking paints
// `currentColor` through the glyph instead, which means it is the same
// $hero-brand as "Gamebot Premium" by inheritance — one colour, not two that
// have to be kept in step.
//
// Sized in em so it tracks the heading through both breakpoints. The height
// is the viewBox ratio (51x43), not `auto`: a masked element has no intrinsic
// size to derive one from.
.premium-headline-crown {
    display: inline-block;
    width: 0.82em;
    height: 0.69em;
    margin-left: 0.08em;
    vertical-align: -0.01em;
    background-color: currentColor;
    -webkit-mask: url('~@/assets/images/premium/crown.svg') no-repeat center / contain;
    mask: url('~@/assets/images/premium/crown.svg') no-repeat center / contain;
}

.premium-hero-actions {
    margin-top: 2.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

// The mock's pill: 160x42 at radius 38. Written here rather than as a Buefy
// type because no existing type carries this pairing, and `is-gold` on a gold
// field is invisible.
.premium-cta.button {
    background-color: $hero-pill;
    color: $hero-pill-label;
    border: none;
    border-radius: 38px;
    min-width: 160px;
    height: 42px;
    font-weight: 500;
    letter-spacing: -0.05em;
    transition: filter 0.15s ease;

    &:hover, &:focus { background-color: $hero-pill; color: $hero-pill-label; filter: brightness(1.15); }
}

.premium-hero-note {
    color: $hero-note;
    font-weight: 500;
    font-size: 1rem;
    letter-spacing: -0.05em;
}

.hero-note-crown {
    display: inline-block;
    width: 0.9rem;
    height: auto;
    vertical-align: -0.05em;
    margin-left: 0.2rem;
}

// The shop artwork down the right. `repeat-y` with the tile sized to the
// column width: the asset is built to join top-to-bottom, so it fills the
// band at any height instead of ending mid-row the way a fixed crop does.
// The shop artwork, running the full height of the band.
//
// Absolute, and pulled out by exactly the hero's padding, so it meets the top
// and bottom edges rather than stopping where the content box does — the band
// has 150px/90px of padding, and as a flex child the artwork started and
// ended inside it with gold above and below.
//
// Right-aligned to .premium-hero-inner, so it sits on the container's edge at
// any width instead of drifting with the viewport.
.premium-hero-pattern {
    position: absolute;
    top: -$hero-pad-top;
    bottom: -$hero-pad-bottom;
    right: $hero-pattern-inset;
    width: 361px;
    background-image: url('~@/assets/images/premium/hero-pattern.png');
    background-repeat: repeat-y;
    background-position: top center;
    background-size: 100% auto;

    // Below the sidebar-ish breakpoint the copy needs the full width, and a
    // 361px column would squeeze the sentence into a ribbon.
    @include touch { display: none; }
}

// Body

.premium-body {
    max-width: $widescreen;
    margin: 0 auto;
    padding: 3rem 20px 5rem;

    @include mobile { padding: 2rem 1rem 3rem; }

    > .box,
    > section,
    > .columns {
        margin-bottom: 1.5rem;
    }
}

// Subscription

.subscription-box {
    border-left: 4px solid $gold;

    // A router-link, so the anchor defaults have to be undone: Bulma's `.box`
    // supplies the surface, and inheriting the text colour keeps the heading
    // and detail reading as copy rather than as a link.
    display: block;
    color: inherit;

    &:hover {
        color: inherit;
        border-left-width: 6px;
        // Enough to register as a target without the card jumping.
        transform: translateY(-1px);
    }

    // A card-sized target needs a visible focus ring; the button it replaced
    // had one for free.
    &:focus-visible {
        outline: 2px solid $gold;
        outline-offset: 2px;
    }

    transition: transform 0.12s ease, border-left-width 0.12s ease;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
        &:hover { transform: none; }
    }

    // Ending and ended are not good news, so they don't get the gold.
    &.is-cancelling,
    &.is-lapsed {
        border-left-color: $grey-light;
        &:focus-visible { outline-color: $grey-light; }
    }
}

// Reads as the affordance it is, without pretending to be a button — the
// destination is a tab on your own profile, not a payment action.
.subscription-go {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    font-weight: 600;
    white-space: nowrap;
    color: $grey-dark;
}

.subscription-level {
    flex-wrap: wrap;
    gap: 1rem;
}

.subscription-state {
    font-weight: bold;
    font-size: 1.1rem;
}

.subscription-state-crown {
    display: inline-block;
    width: 0.95rem;
    height: auto;
    vertical-align: -0.1em;
    margin-right: 0.1rem;
}

.subscription-detail {
    font-size: 0.9rem;
    opacity: 0.72;
}

// The turn from showing to asking
//
// The homepage closes on a rounded conic-gradient panel with a heading and one
// button; this is the same shape in Premium's colour, so the two read as the
// same product rather than two sites. Same angles as the homepage's, in gold
// rather than the brand spectrum.

.premium-turn {
    // The panel is the element here, so the section adds nothing but rhythm.
    margin: 2.5rem 0;
}

.turn-panel {
    border-radius: 12px;
    // Saturated goldenrod rather than $gold, and darkened in the stops
    // themselves rather than under a scrim. A brown scrim over pale gold
    // desaturates on the way down and lands on olive; taking the hues down
    // directly keeps them gold.
    //
    // The ceiling here is set by the white text, not by taste: white needs
    // 3:1 against the *lightest* point a conic blend passes through, and
    // #B8860B is almost exactly where that lands. Anything brighter — #D4AF37,
    // or the #E3C858 this started from — measures 2.1–2.4:1 and is unreadable
    // however nice the swatch looks on its own. Gold is a light colour; white
    // on it is a genuine constraint rather than a preference.
    background:
        conic-gradient(
            from 127.75deg at 50% 43.53%,
            #B8860B 0deg,
            #A87A0A 100deg,
            #8A6408 190deg,
            #9E7209 260deg,
            #B07F0A 320deg,
            #B8860B 360deg
        )
        #A87A0A;
    padding: 4rem 2rem;
    text-align: center;

    @include mobile { padding: 3rem 1.25rem; }
}

// No text-shadow: the scrim above carries the contrast on its own, and a glow
// behind white text on gold read as a smudge rather than depth.
.turn-title.title {
    color: $white;
    margin-bottom: 0.5rem;
}

// Set at 20px/700 deliberately. Without the shadow, white on the gradient's
// lightest region lands around 3.4:1 — over the 3:1 floor for large text, under
// the 4.5:1 one for body. At bold 20px this *is* large text, so the size is
// doing accessibility work, not only design work. Shrink it and it fails.
.turn-sub {
    color: $white;
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1.75rem;
}

// Light button, gold label. $gold itself measures about 1.5:1 on $light and is
// illegible; this is the same hue taken down far enough to read.
.turn-cta.button {
    color: $gold-text;

    &:hover,
    &:focus { color: $gold-text-dark; }
}

// Plans

.plan-columns { margin-top: 1.5rem; }

// Sits inside the section's subtitle, so it needs to look like a link without
// an href — there isn't one, because this scrolls rather than navigates.
// Gold, not Bulma's link colour. Bulma links are $primary — a bright cyan
// measuring about 1.6:1 on white, unreadable at this size — and the computed
// dark cyan that replaced it was the only teal in this part of the page. Gold
// is Premium's colour everywhere else here, so the link now belongs to the
// page rather than sitting outside its palette.
.pass-link {
    white-space: nowrap;
    cursor: pointer;
    font-weight: bold;
    margin-left: 0.35rem;
    color: $gold-text;

    &:hover { color: $gold-text-dark; }

    // Buefy's is-small icon is a fixed 16px box regardless of the glyph
    // inside it, and the angle-right chevron is only ~8px wide — so half
    // that box is empty space sitting between the word "Pass" and the
    // arrow. Pulled in to close the gap rather than the glyph looking
    // stranded a beat after the text it belongs to.
    .icon {
        margin-left: -0.2rem;
    }
}

.premium-rule {
    background-color: rgba(127, 127, 127, 0.2);
    height: 1px;
    margin: 3rem 0;
}

// Both scroll targets clear the fixed navbar. `.navbar-background.pinned` is
// position:fixed at 52px tall, so a section scrolled to the top of the
// viewport has its heading painted underneath it. 52px plus room to breathe.
#plans,
#server-pass {
    scroll-margin-top: 5rem;
}

.plan-box {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    // Gold is Premium's colour, the same as Gamebot Gold elsewhere in the
    // product. Reserved for the recommended column so it marks one thing
    // rather than being the background noise of all three.
    border: 2px solid transparent;

    &.is-recommended { border-color: $gold; }
}

// The lockup. Deliberately restrained — the wordmark is a name here, not a
// banner, and the passes are a count rather than an illustration. Undersized
// reads as a mark; oversized reads as clip art.
// One line: the name and what it carries, read together. Stacked, they read
// as two separate marks; side by side the pass becomes part of the name.
.plan-name {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
    // Never break the lockup across lines — a wordmark orphaned above its own
    // pass is exactly what putting them on one line was meant to fix.
    flex-wrap: nowrap;
}

// Inter bold, matching the type around it rather than a glyph from the
// artwork. Reads as the "+" in an equation: Premium plus what it carries.
.plan-plus {
    flex: 0 0 auto;
    font-weight: 700;
    font-size: 0.95rem;
    opacity: 0.55;
    line-height: 1;
}

// Fixed, never flexed. Letting it shrink to fit meant the mark rendered at
// 196px, 190px and 184px across the three cards — the same logo at three
// sizes, which reads as a mistake. The short mark fits at full size in the
// tightest column, so nothing has to give.
.plan-wordmark {
    display: block;
    flex: 0 0 auto;
    width: 9rem;
    height: auto;
}

.plan-passes {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex: 0 0 auto;
}

// Sized by height, not width: the stacked drawing is far wider than the
// single one, and matching widths would make one pass tower over three.
.plan-pass {
    display: block;
    height: 1.05rem;
    width: auto;
}

.plan-multiplier {
    font-weight: 700;
    font-size: 0.85rem;
    opacity: 0.75;
    white-space: nowrap;
}

.plan-servers {
    font-weight: bold;
    opacity: 0.55;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.75rem;
    margin-bottom: 1rem;
}

.plan-price { white-space: nowrap; }

.plan-amount {
    font-size: 2.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
}

.plan-interval {
    opacity: 0.6;
    font-weight: bold;
}

// Holds the row's height whether or not there's a saving to show, so the
// three columns' checklists start on the same line.
.plan-compare {
    min-height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
}

.plan-list { opacity: 0.5; }

.plan-list-placeholder {
    font-size: 0.85rem;
    opacity: 0.6;
}

.plan-checklist {
    text-align: left;
    margin-bottom: 1.5rem;
    // Pushes the button to the bottom of the box, so the three CTAs line up
    // even though the lists above them differ in length.
    flex: 1 1 auto;

    li {
        // Hanging indent. The tick is inline by default, so a line that wraps
        // starts underneath it and the list loses its left edge. Nothing wraps
        // at the widths this renders at today — this is what keeps that true
        // if a line ever gets longer.
        display: flex;
        align-items: flex-start;
        gap: 0.4rem;
    }

    li + li { margin-top: 0.5rem; }
}

.plan-unit {
    margin-top: 0.6rem;
    opacity: 0.7;
}

.plan-fineprint { margin-top: 1.25rem; }

// The Server Pass
//
// The artwork is an icon here, sitting in a bubble that half-overlaps the top
// edge — the same device the shop's purchase modal uses for its credits. It
// stood in as the section's headline before, which left the section with no
// title of its own and an image doing a job images are bad at.

.pass-section {
    position: relative;
    text-align: center;
    // Clears the half of the bubble that hangs above the box.
    margin: 3.25rem 0 1.5rem;
    padding: 3.25rem 2rem 2rem;

    @include mobile { padding: 3rem 1.25rem 1.5rem; }
}

.pass-bubble {
    position: absolute;
    top: -2.25rem;
    left: calc(50% - 2.25rem);
    width: 4.5rem;
    height: 4.5rem;
    border-radius: 50%;
    background: $white;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
}

.pass-icon {
    // Small enough to read as a mark rather than a picture, with room left
    // inside the circle so the pass isn't jammed against its edge.
    width: 2.5rem;
    height: auto;
    display: block;
}

.pass-title.title { margin-bottom: 0.25rem; }

.pass-coming-soon {
    vertical-align: middle;
    margin-left: 0.5rem;
    font-size: 0.75rem;
}

// Carries the gap the lede used to hold open, now that the header is just a
// title and one line under it.
.pass-tagline {
    font-weight: 700;
    opacity: 0.8;
    margin-bottom: 2rem;
}

.pass-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    // The features read left-aligned even though the header above them is
    // centred; centred body copy under an icon is a pattern that stops
    // working the moment a line wraps.
    text-align: left;

    @include touch { grid-template-columns: 1fr; gap: 1rem; }
}

.pass-feature.media {
    // Bulma's .media adds a top border and padding between siblings, which
    // reads as a list here rather than a set of features.
    border-top: 0;
    padding-top: 0;
    margin-top: 0;
    align-items: flex-start;
}

.feature-icon {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: rgba(127, 127, 127, 0.12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.feature-title { font-weight: bold; }

.feature-body {
    font-size: 0.9rem;
    opacity: 0.72;
}

// Leaderboards are the reason the pass exists, so the row that says so gets
// the colour — gold, Premium's colour throughout the rest of the page, so
// this row reads as part of the same pitch rather than borrowing the pass's
// own cyan. $gold itself is too light to set text or an icon in (~1.5:1 on
// white), so both the icon and the title use $gold-text.
.pass-feature.is-highlighted {
    .feature-icon {
        background: rgba($gold, 0.28);
        color: $gold-text;
    }

    .feature-title { color: $gold-text; }

    // Lifted from the 0.72 the others carry: the point of highlighting the
    // row is that this one gets read, and dimmed body copy works against it.
    .feature-body { opacity: 0.85; }
}

// FAQ

.faq-box { height: 100%; }

.faq-q {
    font-weight: bold;
    margin-bottom: 0.25rem;
}

.faq-a {
    font-size: 0.9rem;
    opacity: 0.72;
}
</style>
